import type { ReactElement } from "react";

import styles from "./Avatar.module.css";

export type AvatarShape = "circle" | "square";

export interface AvatarProps {
  /** The person or organisation; the avatar shows its initials. */
  readonly name: string;
  /** `circle` for a person, `square` for an organisation (the mockup's `.avatar` and `.org-av`). */
  readonly shape?: AvatarShape | undefined;
  /**
   * Decorative by default: an avatar almost always sits next to the written name, and a screen
   * reader would otherwise read the name twice. `labelled` makes it an image named by `name`.
   */
  readonly labelled?: boolean | undefined;
  /** The person's picture (mockup v3 P1, `MeSummary.avatar_url`); the initials when it is null. */
  readonly imageUrl?: string | null | undefined;
}

/** The first letter of the first two words, upper-cased; `?` for an empty name. */
export function initialsOf(name: string): string {
  const words = name.trim().split(/\s+/).filter((word) => word.length > 0);
  if (words.length === 0) return "?";
  return words
    .slice(0, 2)
    .map((word) => word.charAt(0).toLocaleUpperCase("es"))
    .join("");
}

/** Initials on a neutral tile; never a fabricated photo. */
export function Avatar({ name, shape = "circle", labelled = false, imageUrl = null }: AvatarProps): ReactElement {
  const className = [styles.avatar, styles[shape]].join(" ");
  // The picture says nothing the name next to it does not, so its text alternative is empty.
  const face = imageUrl === null ? initialsOf(name) : <img className={styles.picture} src={imageUrl} alt={""} />;
  if (labelled) {
    return (
      <span className={className} role="img" aria-label={name}>
        {face}
      </span>
    );
  }
  // Decorative initials are drawn by the stylesheet (`data-initials`), not written as text: axe reads
  // aria-hidden text as shown, and a control next to it would have to repeat them in its name (WCAG 2.5.3).
  return (
    <span className={className} aria-hidden="true" data-initials={imageUrl === null ? initialsOf(name) : undefined}>
      {imageUrl === null ? null : face}
    </span>
  );
}
